import * as THREE from 'three';
import { locateTrackPosition, type Track } from '../track/TrackLoader';
import { buildMeshCollider, type MeshCollider, type MeshTriangle2D, type ObstacleObb } from '../game/Collision';
import {
  canyonOpenness,
  canyonWallDistance,
  canyonWallHeight,
  isCanyonLandscape,
  isCanyonV3Landscape,
  type LandscapeOptions,
  type LandscapeVersion,
} from './Landscape';
import { TERRAIN_BASE_Y } from './Terrain';
import { installAerialPerspectiveForObject } from './AerialPerspective';
import { installValueCompositionForObject, tagValueBucketDeep } from './ValueComposition';

export interface CanyonWallTextureSet {
  albedo: THREE.Texture | null;
  normal: THREE.Texture | null;
  arm: THREE.Texture | null;
}

/**
 * Procedural canyon scenery for the Twin Suns Autodrome.
 *
 * Owns everything that sits ON the terrain — the terrain mesh and far-distance
 * silhouette horizon are rendered by `Terrain.ts`. Pass `floorSampler` from
 * `buildTerrain()` so each prop sits flush on the actual hill it's standing on.
 *
 * Layers, in order:
 *   1. Canyon shoulders— ridge-multifractal strips just outside the walls.
 *   2. Rock formations — 4 archetypes (spire/mesa/boulder/archFragment),
 *                        InstancedMesh per archetype, biome-zoned placement.
 *   3. Hero landmarks  — large unique pieces at the highest-curvature corners.
 *   4. Pass-through arches — drive-under rock arches.
 *   5. Start/finish gantry — bridge structure over the start line.
 *   6. Sector flags    — checkpoint markers at 0%, 33%, 67%.
 *   7. Distance pylons — thin posts on long straight stretches.
 *
 * 3D-model integration: pass a `SceneryKit` whose `BufferGeometry` fields
 * override the procedural archetype geometries. Geometries should be unit-
 * sized (≈1 wide, ≈1 tall, base at y=0) so per-instance scale produces the
 * right size.
 */

const ROCK = 0x9a6040;
const ROCK_DARK = 0x6d412d;
const ROCK_LIGHT = 0xc7895a;
// Cool violet tint pulled into shadowed valley rocks; warm rim tint pulled
// onto sunlit ridge rocks. Matches the terrain palette in `Terrain.ts` so
// rocks read as part of the same canyon, not floating on top of it.
const ROCK_VALLEY_SHADOW = new THREE.Color(0x4d3a4a);
const ROCK_RIDGE_LIT = new THREE.Color(0xd49464);
const ARCH_ROCK = 0x87543a;
const PYLON = 0x3a2a1d;
const PYLON_BAND = 0xe6d5a8;
const FLAG_PRIMARY = 0xd95f3a;
const FLAG_SECONDARY = 0xf3e8c7;
const GANTRY = 0x4a3322;
const START_BRIDGE_LOW_DECK_MAX_Y = 0.075;
const START_BRIDGE_LOW_DECK_NORMAL_Y = 0.55;
const START_BRIDGE_CORRIDOR_MARGIN_U = 0.025;
const START_BRIDGE_PROXY_HALF_ACROSS = 1.4;
const START_BRIDGE_PROXY_HALF_ALONG = 2.4;
const START_BRIDGE_PROXY_INSET = 1.0;

export type RockArchetype = 'spire' | 'mesa' | 'boulder' | 'archFragment';

export interface RockPlacement {
  archetype: RockArchetype;
  x: number;
  z: number;
  radius: number;
  height: number;
  rotationY: number;
  tilt: number;
  shade: number;
}

export interface HeroLandmarkSpec {
  archetype: 'megaSpire' | 'megaMesa';
  x: number;
  z: number;
  radius: number;
  height: number;
  scaleX?: number;
  scaleZ?: number;
  wallSide?: 1 | -1;
  rotationY: number;
  centerlineIndex: number;
}

export interface OverpassSpec {
  x: number;
  z: number;
  heading: number;
  span: number;
  clearance: number;
  depth: number;
}

export interface SceneryKit {
  // Instanced archetypes — drawn via InstancedMesh, share one rock material.
  spire?: THREE.BufferGeometry;
  mesa?: THREE.BufferGeometry;
  boulder?: THREE.BufferGeometry;
  archFragment?: THREE.BufferGeometry;
  megaSpire?: THREE.BufferGeometry;
  megaMesa?: THREE.BufferGeometry;
  // Optional AI-generated (e.g. Tripo3D) cliff chunk variants. Each entry is
  // a unit-sized BufferGeometry (≈1×1×1, base at y=0) extracted from a GLB.
  // When present, `buildCanyonV3Spires` swaps the procedural cylinder spires
  // for instanced random picks across this array — adds real geometric
  // variety where curated spires would otherwise read as prisms.
  // Drop-in: place GLBs at /public/rocks/cliff_chunk_{01..05}.glb.
  canyonCliffChunks?: THREE.BufferGeometry[];
  // Hero slots — full Object3D scenes with authored materials. Cloned per
  // placement so we keep the GLB look (steampunk tower / arena) instead of
  // overriding with the rock material. Loaded by `SceneryAssets.ts`.
  heroTower?: THREE.Object3D;
  heroArena?: THREE.Object3D;
  // Authored start/finish bridge (replaces the procedural gantry when present).
  // Loaded by `SceneryAssets.ts`; if absent, `buildStartFinishGantry` falls
  // back to the cylinder-leg + beam + checker-banner gantry.
  heroStartBridge?: THREE.Object3D;
}

export type FloorSampler = (x: number, z: number) => number;

export interface SceneryOptions extends LandscapeOptions {
  kit?: SceneryKit;
  floorSampler?: FloorSampler;
  canyonWallTextures?: CanyonWallTextureSet;
  useBatchedMesh?: boolean;
}

const FLAT_FLOOR: FloorSampler = () => -0.15;

export async function buildScenery(
  track: Track,
  options: SceneryOptions = {},
): Promise<{ group: THREE.Group; obstacles: ObstacleObb[]; meshColliders: MeshCollider[] }> {
  const group = new THREE.Group();
  group.name = 'procedural-canyon-scenery';
  const kit = options.kit ?? {};
  const sampler = options.floorSampler ?? FLAT_FLOOR;
  const canyon = isCanyonLandscape(options.version);
  const canyonV3 = isCanyonV3Landscape(options.version);
  const obstacles: ObstacleObb[] = [];
  const meshColliders: MeshCollider[] = [];

  if (canyonV3) {
    // canyon-v3 skips the procedural rock/wall/landmark furniture, but the
    // start-finish gate still belongs over the start line. Renders the
    // authored bridge GLB if loaded; the procedural fallback is suppressed
    // here because canyon-v3 is intentionally unfurnished.
    if (kit.heroStartBridge) group.add(buildStartFinishGantry(track, kit, obstacles, meshColliders));
    tagValueBucketDeep(group, 'bg');
    installAerialPerspectiveForObject(group);
    installValueCompositionForObject(group, 'bg');
    return { group, obstacles, meshColliders };
  }

  if (canyon) group.add(buildCanyonWalls(track, sampler));
  group.add(buildCanyonShoulders(track, options.version));
  group.add(buildRockFormations(track, kit, sampler, options.version, obstacles, options.useBatchedMesh ?? true));
  group.add(buildHeroLandmarks(track, kit, sampler, obstacles));
  if (!canyon) group.add(buildPassThroughArches(track, sampler));
  group.add(buildStartFinishGantry(track, kit, obstacles, meshColliders));
  group.add(buildSectorFlags(track));
  group.add(buildDistancePylons(track, sampler));
  tagValueBucketDeep(group, 'bg');
  installAerialPerspectiveForObject(group);
  installValueCompositionForObject(group, 'bg');
  return { group, obstacles, meshColliders };
}

// ────────────────────────────────────────────────────────────────────────────
// Test exports
// ────────────────────────────────────────────────────────────────────────────

export function __test__generateOverpasses(track: Track): OverpassSpec[] {
  const fractions = [0.18, 0.43, 0.72];
  return fractions.map((fraction, i) => {
    const index = Math.floor(track.centerline.length * fraction) % track.centerline.length;
    const c = track.centerline[index];
    const tangent = track.tangents[index];
    const corridorWidth = c.widthLeft + c.widthRight + track.wallBuffer * 2;
    return {
      x: c.x,
      z: c.z,
      heading: Math.atan2(tangent.tx, tangent.tz),
      span: corridorWidth + 12 + i * 2,
      clearance: 8.5 + i * 0.8,
      depth: 10 + i * 2,
    };
  });
}

export function __test__generateRockPlacements(
  track: Track,
  seed = 0x51a5,
  version: LandscapeVersion = 'classic',
): RockPlacement[] {
  const placements: RockPlacement[] = [];
  const rng = mulberry32(seed);
  const stride = 4;
  const canyon = isCanyonLandscape(version);

  for (let i = 0; i < track.centerline.length; i += stride) {
    const c = track.centerline[i];
    const tangent = track.tangents[i];
    const nx = -tangent.tz;
    const nz = tangent.tx;

    const biome = fbm2(c.x * 0.0042, c.z * 0.0042, 3);
    const openness = canyon ? canyonOpenness(track.cumulativeLength[i], 1) : desertOpenness(track.cumulativeLength[i]);
    const density = canyon ? 0.12 + biome * 0.36 + openness * 0.2 : 0.18 + biome * 0.7;

    for (let attempt = 0; attempt < 3; attempt++) {
      if (rng() > density) continue;
      const side = rng() > 0.5 ? 1 : -1;
      const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
      const canyonSetback = canyon
        ? canyonWallDistance(canyonOpenness(track.cumulativeLength[i], side)) * (0.45 + rng() * 0.46)
        : 0;
      const baseOffset = halfWidth + track.wallBuffer + (canyon ? 18 : 6);
      const lateral = baseOffset + canyonSetback + rng() * (canyon ? 70 + biome * 105 : 28 + biome * 60);
      const along = (rng() - 0.5) * 14;
      const px = c.x + nx * side * lateral + tangent.tx * along;
      const pz = c.z + nz * side * lateral + tangent.tz * along;

      const tp = locateTrackPosition(track, px, pz);
      const leftClearance = tp.signedLateral - (tp.widthLeft + track.wallBuffer);
      const rightClearance = -tp.signedLateral - (tp.widthRight + track.wallBuffer);
      const outsideLeft = leftClearance > 4;
      const outsideRight = rightClearance > 4;
      if (!outsideLeft && !outsideRight) continue;

      const clearance = Math.max(leftClearance, rightClearance);
      const archetype = pickRockArchetype(rng, biome, clearance, version);
      const size = archetypeSize(archetype, rng, biome, clearance);
      placements.push({
        archetype,
        x: px,
        z: pz,
        radius: size.radius,
        height: size.height,
        rotationY: rng() * Math.PI * 2,
        tilt: (rng() - 0.5) * 0.18,
        shade: rng(),
      });
    }
  }
  return placements;
}

export function __test__generateHeroLandmarks(track: Track): HeroLandmarkSpec[] {
  const n = track.centerline.length;
  if (n < 16) return [];

  const window = Math.max(3, Math.round(n * 0.012));
  const samples: { i: number; turn: number; cross: number }[] = [];
  for (let i = 0; i < n; i++) {
    const a = track.tangents[(i - window + n) % n];
    const b = track.tangents[(i + window) % n];
    const cross = a.tx * b.tz - a.tz * b.tx;
    const dot = THREE.MathUtils.clamp(a.tx * b.tx + a.tz * b.tz, -1, 1);
    samples.push({ i, turn: Math.acos(dot), cross });
  }
  samples.sort((p, q) => q.turn - p.turn);

  const minSpacing = Math.max(8, Math.floor(n / 7));
  const picks: HeroLandmarkSpec[] = [];
  const startArena = makeStartArenaLandmark(track);
  if (startArena) picks.push(startArena);

  for (const s of samples) {
    if (picks.length >= 3) break;
    if (s.turn < 0.18) break;
    if (picks.some((p) => circularDistance(p.centerlineIndex, s.i, n) < minSpacing)) continue;

    const c = track.centerline[s.i];
    const tangent = track.tangents[s.i];
    const nx = -tangent.tz;
    const nz = tangent.tx;
    // Outside of the corner — opposite of the curvature direction.
    const side: 1 | -1 = s.cross > 0 ? -1 : 1;
    const halfWidth = side > 0 ? c.widthLeft : c.widthRight;

    // The closed track may loop near the corner's "outside," so the chosen
    // offset must clear *any* nearby segment, not just the local one.
    // Try increasing offsets until the nearest segment is comfortably outside.
    let placed: { x: number; z: number; offset: number } | null = null;
    const baseOffset = halfWidth + track.wallBuffer + 26 + s.turn * 18;
    for (const extra of [0, 14, 30, 50]) {
      const offset = baseOffset + extra;
      const px = c.x + nx * side * offset;
      const pz = c.z + nz * side * offset;
      const tp = locateTrackPosition(track, px, pz);
      const outsideLeft = tp.signedLateral > tp.widthLeft + track.wallBuffer + 18;
      const outsideRight = tp.signedLateral < -tp.widthRight - track.wallBuffer - 18;
      if (outsideLeft || outsideRight) {
        placed = { x: px, z: pz, offset };
        break;
      }
    }
    if (!placed) continue;

    picks.push({
      archetype: 'megaSpire',
      x: placed.x,
      z: placed.z,
      radius: 6,
      height: 32 + s.turn * 10,
      rotationY: Math.atan2(-nx * side, -nz * side),
      centerlineIndex: s.i,
    });
  }
  return picks;
}

export function __test__removeStartBridgeLowDeckFaces(
  bridge: THREE.Object3D,
  corridorHalfU: number,
): number {
  return removeStartBridgeLowDeckFaces(bridge, corridorHalfU);
}


function makeStartArenaLandmark(track: Track): HeroLandmarkSpec | null {
  const c = track.centerline[0];
  const tangent = track.tangents[0];
  const heading = Math.atan2(tangent.tx, tangent.tz);
  const nx = -tangent.tz;
  const nz = tangent.tx;

  for (const side of [1, -1] as const) {
    const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
    const modelScale = 30;
    const modelDepth = modelScale * 0.74;
    const offset = halfWidth + track.wallBuffer + modelDepth * 0.5 - 1.5;
    const x = c.x + nx * side * offset;
    const z = c.z + nz * side * offset;
    const tp = locateTrackPosition(track, x, z);
    const outsideLeft = tp.signedLateral > tp.widthLeft + track.wallBuffer + 3;
    const outsideRight = tp.signedLateral < -tp.widthRight - track.wallBuffer - 3;
    if (!outsideLeft && !outsideRight) continue;

    return {
      archetype: 'megaMesa',
      x,
      z,
      radius: modelScale,
      height: modelScale,
      scaleX: 8,
      scaleZ: 40,
      wallSide: side,
      rotationY: heading,
      centerlineIndex: 0,
    };
  }

  return null;
}


function buildCanyonWalls(track: Track, sampler: FloorSampler): THREE.Group {
  const group = new THREE.Group();
  group.name = 'procedural-canyon-walls';
  group.add(buildCanyonWall(track, 1, sampler));
  group.add(buildCanyonWall(track, -1, sampler));
  return group;
}

function buildCanyonWall(track: Track, side: 1 | -1, sampler: FloorSampler): THREE.Mesh {
  const n = track.centerline.length;
  const rows = 4;
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ringA = buildCanyonWallRing(track, i, side, sampler);
    const ringB = buildCanyonWallRing(track, j, side, sampler);
    if (!isSafeCanyonWallSegment(track, ringA, ringB)) continue;

    const base = positions.length / 3;
    for (const p of [...ringA, ...ringB]) {
      positions.push(p.x, p.y, p.z);
      colors.push(p.color.r, p.color.g, p.color.b);
    }

    for (let r = 0; r < rows; r++) {
      const a = base + r;
      const b = base + ((r + 1) % rows);
      const c = base + rows + r;
      const d = base + rows + ((r + 1) % rows);
      if (side > 0) indices.push(a, b, d, a, d, c);
      else indices.push(a, d, b, a, c, d);
    }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    indices.push(base + rows, base + rows + 2, base + rows + 1, base + rows, base + rows + 3, base + rows + 2);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geom,
    new THREE.MeshStandardMaterial({
      roughness: 1,
      metalness: 0,
      vertexColors: true,
      flatShading: true,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = side > 0 ? 'left-procedural-canyon-wall' : 'right-procedural-canyon-wall';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

type CanyonWallVertex = {
  x: number;
  y: number;
  z: number;
  color: THREE.Color;
};

function buildCanyonWallRing(
  track: Track,
  centerlineIndex: number,
  side: 1 | -1,
  sampler: FloorSampler,
): CanyonWallVertex[] {
  const c = track.centerline[centerlineIndex];
  const { tx, tz } = track.tangents[centerlineIndex];
  const nx = -tz;
  const nz = tx;
  const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
  const distance = track.cumulativeLength[centerlineIndex];
  const openness = canyonOpenness(distance, side);
  const roughness = ridgeFbm(distance * 0.014 + side * 9.1, side * 18.7, 3);
  const wallOffset = halfWidth + track.wallBuffer + canyonWallDistance(openness);
  const footJitter = (fbm2(distance * 0.025, side * 3.1, 3) - 0.5) * 14;
  const baseOffset = wallOffset + footJitter;
  const height = canyonWallHeight(openness, roughness);
  const lean = 4 + openness * 22 + roughness * 9;
  const back = 20 + openness * 56;
  const footOffset = resolveCanyonWallOffset(track, centerlineIndex, side, baseOffset - 5, 8);
  const footX = c.x + nx * side * footOffset;
  const footZ = c.z + nz * side * footOffset;
  const baseY = sampler(footX, footZ) - 0.4;
  const rowData = [
    { offset: footOffset, y: baseY, t: 0 },
    { offset: resolveCanyonWallOffset(track, centerlineIndex, side, baseOffset + lean * 0.18, 8), y: baseY + height * 0.34, t: 0.34 },
    { offset: resolveCanyonWallOffset(track, centerlineIndex, side, baseOffset + lean, 10), y: baseY + height * 0.86, t: 0.86 },
    { offset: resolveCanyonWallOffset(track, centerlineIndex, side, baseOffset + lean + back, 12), y: baseY + height * (0.96 + roughness * 0.16), t: 1 },
  ];
  const valley = new THREE.Color(0x7f4535);
  const face = new THREE.Color(0x9f5738);
  const rim = new THREE.Color(0xd59a62);
  const shadow = new THREE.Color(0x38253b);
  const strata = Math.sin(distance * 0.031 + side * 2.4) * 0.5 + 0.5;
  const tmp = new THREE.Color();

  return rowData.map((p, r) => {
    tmp.copy(valley).lerp(face, p.t);
    tmp.lerp(rim, Math.max(0, p.t - 0.68) * 0.8);
    tmp.lerp(shadow, (1 - openness) * (0.18 + strata * 0.16) * (r < 2 ? 0.7 : 1));
    return {
      x: c.x + nx * side * p.offset,
      y: p.y,
      z: c.z + nz * side * p.offset,
      color: tmp.clone(),
    };
  });
}

function isSafeCanyonWallSegment(
  track: Track,
  ringA: CanyonWallVertex[],
  ringB: CanyonWallVertex[],
): boolean {
  const rows = ringA.length;
  const triSafe = (a: CanyonWallVertex, b: CanyonWallVertex, c: CanyonWallVertex): boolean => {
    const x = (a.x + b.x + c.x) / 3;
    const z = (a.z + b.z + c.z) / 3;
    return wallCorridorClearance(track, x, z) > 2;
  };
  for (let r = 0; r < rows; r++) {
    const next = (r + 1) % rows;
    if (!triSafe(ringA[r], ringA[next], ringB[next])) return false;
    if (!triSafe(ringA[r], ringB[next], ringB[r])) return false;
  }
  if (!triSafe(ringA[0], ringA[1], ringA[2])) return false;
  if (!triSafe(ringA[0], ringA[2], ringA[3])) return false;
  if (!triSafe(ringB[0], ringB[2], ringB[1])) return false;
  if (!triSafe(ringB[0], ringB[3], ringB[2])) return false;
  return true;
}

function wallCorridorClearance(track: Track, x: number, z: number): number {
  const tp = locateTrackPosition(track, x, z);
  const leftClearance = tp.signedLateral - (tp.widthLeft + track.wallBuffer);
  const rightClearance = -tp.signedLateral - (tp.widthRight + track.wallBuffer);
  return Math.max(leftClearance, rightClearance);
}

function resolveCanyonWallOffset(
  track: Track,
  centerlineIndex: number,
  side: 1 | -1,
  desiredOffset: number,
  minClearance: number,
): number {
  const c = track.centerline[centerlineIndex];
  const { tx, tz } = track.tangents[centerlineIndex];
  const nx = -tz;
  const nz = tx;
  let offset = desiredOffset;
  for (let attempt = 0; attempt < 48; attempt++) {
    const x = c.x + nx * side * offset;
    const z = c.z + nz * side * offset;
    const tp = locateTrackPosition(track, x, z);
    const leftClearance = tp.signedLateral - (tp.widthLeft + track.wallBuffer);
    const rightClearance = -tp.signedLateral - (tp.widthRight + track.wallBuffer);
    if (Math.max(leftClearance, rightClearance) > minClearance) return offset;
    offset += 8;
  }
  return offset;
}

function buildCanyonShoulders(track: Track, version: LandscapeVersion | undefined): THREE.Group {
  const group = new THREE.Group();
  group.name = 'broken-canyon-shoulders';
  group.add(buildShoulderStrip(track, 1, version));
  group.add(buildShoulderStrip(track, -1, version));
  return group;
}

function buildShoulderStrip(track: Track, side: 1 | -1, version: LandscapeVersion | undefined): THREE.Mesh {
  if (isCanyonLandscape(version)) return buildSafeCanyonShoulderStrip(track, side);

  const n = track.centerline.length;
  const positions = new Float32Array(n * 2 * 3);
  const colors = new Float32Array(n * 2 * 3);
  const indices: number[] = [];
  const low = new THREE.Color(ROCK_DARK);
  const high = new THREE.Color(0xd29a63);

  for (let i = 0; i < n; i++) {
    const c = track.centerline[i];
    const { tx, tz } = track.tangents[i];
    const nx = -tz;
    const nz = tx;
    const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
    const distance = track.cumulativeLength[i];
    const openness = isCanyonLandscape(version)
      ? canyonOpenness(distance, side)
      : desertOpenness(distance + side * 43);
    const wallEdge = halfWidth + track.wallBuffer + 1.8;
    const ridge = ridgeFbm(c.x * 0.018, c.z * 0.018, 4);
    const outer = isCanyonLandscape(version)
      ? wallEdge + 10 + openness * 80 + ridge * 18
      : wallEdge + 6 + openness * 30 + ridge * 8;
    const height = isCanyonLandscape(version)
      ? 0.1 + (1 - openness) * (1.8 + ridge * 5.6)
      : 0.04 + (1 - openness) * (0.6 + ridge * 2.6);

    positions[i * 6 + 0] = c.x + nx * side * wallEdge;
    positions[i * 6 + 1] = 0.022;
    positions[i * 6 + 2] = c.z + nz * side * wallEdge;
    positions[i * 6 + 3] = c.x + nx * side * outer;
    positions[i * 6 + 4] = height;
    positions[i * 6 + 5] = c.z + nz * side * outer;

    const tint = low.clone().lerp(high, 0.4 + openness * 0.28 + ridge * 0.08);
    for (let k = 0; k < 2; k++) {
      colors[(i * 2 + k) * 3 + 0] = tint.r;
      colors[(i * 2 + k) * 3 + 1] = tint.g;
      colors[(i * 2 + k) * 3 + 2] = tint.b;
    }
  }

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const a = i * 2;
    const b = i * 2 + 1;
    const c = j * 2;
    const d = j * 2 + 1;
    if (side > 0) indices.push(a, b, d, a, d, c);
    else indices.push(a, d, b, a, c, d);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geom,
    new THREE.MeshStandardMaterial({
      roughness: 1,
      metalness: 0,
      vertexColors: true,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = side > 0 ? 'left-broken-canyon-shoulder' : 'right-broken-canyon-shoulder';
  mesh.receiveShadow = true;
  return mesh;
}

type ShoulderVertex = {
  x: number;
  y: number;
  z: number;
  color: THREE.Color;
};

function buildSafeCanyonShoulderStrip(track: Track, side: 1 | -1): THREE.Mesh {
  const n = track.centerline.length;
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ringA = buildSafeShoulderRing(track, i, side);
    const ringB = buildSafeShoulderRing(track, j, side);
    if (!isSafeShoulderSegment(track, ringA, ringB)) continue;

    const base = positions.length / 3;
    for (const p of [...ringA, ...ringB]) {
      positions.push(p.x, p.y, p.z);
      colors.push(p.color.r, p.color.g, p.color.b);
    }

    if (side > 0) indices.push(base, base + 1, base + 3, base, base + 3, base + 2);
    else indices.push(base, base + 3, base + 1, base, base + 2, base + 3);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geom,
    new THREE.MeshStandardMaterial({
      roughness: 1,
      metalness: 0,
      vertexColors: true,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = side > 0 ? 'left-broken-canyon-shoulder' : 'right-broken-canyon-shoulder';
  mesh.receiveShadow = true;
  return mesh;
}

function buildSafeShoulderRing(
  track: Track,
  centerlineIndex: number,
  side: 1 | -1,
): [ShoulderVertex, ShoulderVertex] {
  const c = track.centerline[centerlineIndex];
  const { tx, tz } = track.tangents[centerlineIndex];
  const nx = -tz;
  const nz = tx;
  const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
  const distance = track.cumulativeLength[centerlineIndex];
  const openness = canyonOpenness(distance, side);
  const ridge = ridgeFbm(c.x * 0.018, c.z * 0.018, 4);
  const wallEdge = halfWidth + track.wallBuffer + 3.2;
  const innerOffset = resolveCanyonWallOffset(track, centerlineIndex, side, wallEdge, 2.4);
  const outerTarget = wallEdge + 10 + openness * 78 + ridge * 16;
  const outerOffset = resolveCanyonWallOffset(
    track,
    centerlineIndex,
    side,
    Math.max(outerTarget, innerOffset + 8),
    2.8,
  );
  const height = 0.08 + (1 - openness) * (1.2 + ridge * 4.2);
  const low = new THREE.Color(ROCK_DARK);
  const high = new THREE.Color(0xd29a63);
  const tint = low.lerp(high, 0.42 + openness * 0.28 + ridge * 0.08);
  const rim = new THREE.Color(0xe0ac6f);

  return [
    {
      x: c.x + nx * side * innerOffset,
      y: 0.014,
      z: c.z + nz * side * innerOffset,
      color: tint.clone().lerp(rim, 0.08),
    },
    {
      x: c.x + nx * side * outerOffset,
      y: height,
      z: c.z + nz * side * outerOffset,
      color: tint.clone().lerp(rim, 0.26),
    },
  ];
}

function isSafeShoulderSegment(
  track: Track,
  ringA: [ShoulderVertex, ShoulderVertex],
  ringB: [ShoulderVertex, ShoulderVertex],
): boolean {
  return isSafeSurfaceTriangle(track, ringA[0], ringA[1], ringB[1], 1)
    && isSafeSurfaceTriangle(track, ringA[0], ringB[1], ringB[0], 1);
}

function isSafeSurfaceTriangle(
  track: Track,
  a: ShoulderVertex,
  b: ShoulderVertex,
  c: ShoulderVertex,
  minClearance: number,
): boolean {
  const samples = [
    [a.x, a.z],
    [b.x, b.z],
    [c.x, c.z],
    [(a.x + b.x) * 0.5, (a.z + b.z) * 0.5],
    [(b.x + c.x) * 0.5, (b.z + c.z) * 0.5],
    [(c.x + a.x) * 0.5, (c.z + a.z) * 0.5],
    [(a.x + b.x + c.x) / 3, (a.z + b.z + c.z) / 3],
  ];
  return samples.every(([x, z]) => wallCorridorClearance(track, x, z) > minClearance);
}

// ────────────────────────────────────────────────────────────────────────────
// Rock formations (4 archetypes × InstancedMesh)
// ────────────────────────────────────────────────────────────────────────────

// Phase A.3: rock instances beyond this distance from the racer's start
// position skip the shadow pass. Real polygons against the sky still read
// at distance — silhouettes don't need self-shadowing to feel solid.
const ROCK_SHADOW_RADIUS_M = 80;

function buildRockFormations(
  track: Track,
  kit: SceneryKit,
  sampler: FloorSampler,
  version: LandscapeVersion | undefined,
  obstacles: ObstacleObb[],
  useBatchedMesh: boolean,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'procedural-rock-formations';
  group.updateMatrix();
  group.matrixAutoUpdate = false;

  const placements = __test__generateRockPlacements(track, 0x51a5, version ?? 'classic');
  const start = track.centerline[0];
  const shadowRadiusSq = ROCK_SHADOW_RADIUS_M * ROCK_SHADOW_RADIUS_M;
  type Bucket = { near: RockPlacement[]; far: RockPlacement[] };
  const buckets = new Map<RockArchetype, Bucket>();
  for (const p of placements) {
    const dx = p.x - start.x;
    const dz = p.z - start.z;
    const isNear = (dx * dx + dz * dz) <= shadowRadiusSq;
    const bucket = buckets.get(p.archetype) ?? { near: [], far: [] };
    if (isNear) bucket.near.push(p);
    else bucket.far.push(p);
    buckets.set(p.archetype, bucket);
    // Spires, mesas, and boulders are solid; arch fragments are partial
    // toroids that read as scenic rather than blocking. Under-fit by 0.85
    // so the racer doesn't bonk on visually-clear approaches.
    if (p.archetype === 'archFragment') continue;
    const half = p.radius * 0.85;
    obstacles.push({
      centerX: p.x,
      centerZ: p.z,
      axisAcrossX: 1,
      axisAcrossZ: 0,
      halfAcross: half,
      halfAlong: half,
    });
  }

  if (useBatchedMesh) {
    const nearBatch = makeRockBatchedMesh('rock-batch-near', buckets, 'near', kit, sampler, true);
    if (nearBatch) group.add(nearBatch);
    const farBatch = makeRockBatchedMesh('rock-batch-far', buckets, 'far', kit, sampler, false);
    if (farBatch) group.add(farBatch);
  } else {
    for (const [archetype, bucket] of buckets) {
      const near = makeRockInstancedMesh(archetype, bucket.near, 'near', kit, sampler, true);
      if (near) group.add(near);
      const far = makeRockInstancedMesh(archetype, bucket.far, 'far', kit, sampler, false);
      if (far) group.add(far);
    }
  }

  return group;
}

function makeRockBatchedMesh(
  name: string,
  buckets: Map<RockArchetype, { near: RockPlacement[]; far: RockPlacement[] }>,
  lod: 'near' | 'far',
  kit: SceneryKit,
  sampler: FloorSampler,
  castShadow: boolean,
): THREE.BatchedMesh | null {
  const entries = [...buckets.entries()]
    .map(([archetype, bucket]) => ({ archetype, placements: bucket[lod] }))
    .filter((entry) => entry.placements.length > 0);
  const instanceCount = entries.reduce((sum, entry) => sum + entry.placements.length, 0);
  if (instanceCount === 0) return null;

  const geometries = entries.map((entry) => ({
    archetype: entry.archetype,
    geometry: kit[entry.archetype] ?? defaultArchetypeGeometry(entry.archetype),
    placements: entry.placements,
  }));
  const maxVertexCount = geometries.reduce(
    (sum, entry) => sum + (entry.geometry.getAttribute('position')?.count ?? 0),
    0,
  );
  const maxIndexCount = geometries.reduce(
    (sum, entry) => sum + (entry.geometry.getIndex()?.count ?? entry.geometry.getAttribute('position')?.count ?? 0),
    0,
  );
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    metalness: 0,
    flatShading: true,
    vertexColors: true,
  });
  const batch = new THREE.BatchedMesh(instanceCount, maxVertexCount, maxIndexCount, mat);
  batch.name = name;
  batch.castShadow = castShadow;
  batch.receiveShadow = true;
  batch.perObjectFrustumCulled = true;
  batch.sortObjects = false;
  batch.updateMatrix();
  batch.matrixAutoUpdate = false;

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const c = new THREE.Color();

  for (const entry of geometries) {
    const geometryId = batch.addGeometry(entry.geometry);
    for (const p of entry.placements) {
      const instanceId = batch.addInstance(geometryId);
      const groundY = sampler(p.x, p.z);
      v.set(p.x, groundY - 0.05, p.z);
      e.set(p.tilt * 0.6, p.rotationY, p.tilt * 0.4);
      q.setFromEuler(e);
      s.set(p.radius, p.height, p.radius);
      m.compose(v, q, s);
      batch.setMatrixAt(instanceId, m);
      rockColorForPlacement(entry.archetype, p, groundY, c);
      batch.setColorAt(instanceId, c);
    }
  }
  batch.computeBoundingBox();
  batch.computeBoundingSphere();
  batch.userData.instanceCount = instanceCount;
  batch.userData.geometryCount = geometries.length;
  return batch;
}

function makeRockInstancedMesh(
  archetype: RockArchetype,
  placements: RockPlacement[],
  lod: 'near' | 'far',
  kit: SceneryKit,
  sampler: FloorSampler,
  castShadow: boolean,
): THREE.InstancedMesh | null {
  if (placements.length === 0) return null;
  const geometry = kit[archetype] ?? defaultArchetypeGeometry(archetype);
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    metalness: 0,
    flatShading: true,
    vertexColors: true,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
  mesh.name = `rock-${archetype}-${lod}-instances`;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = true;
  mesh.updateMatrix();
  mesh.matrixAutoUpdate = false;

  const matrix = new THREE.Matrix4();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const color = new THREE.Color();
  placements.forEach((placement, index) => {
    const groundY = sampler(placement.x, placement.z);
    position.set(placement.x, groundY - 0.05, placement.z);
    euler.set(placement.tilt * 0.6, placement.rotationY, placement.tilt * 0.4);
    quaternion.setFromEuler(euler);
    scale.set(placement.radius, placement.height, placement.radius);
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(index, matrix);
    rockColorForPlacement(archetype, placement, groundY, color);
    mesh.setColorAt(index, color);
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

function rockColorForPlacement(
  archetype: RockArchetype,
  placement: RockPlacement,
  groundY: number,
  target: THREE.Color,
): THREE.Color {
  const tintLight = archetype === 'archFragment' ? new THREE.Color(ARCH_ROCK) : new THREE.Color(ROCK_LIGHT);
  const tintDark = archetype === 'archFragment' ? new THREE.Color(ROCK_DARK).lerp(new THREE.Color(ARCH_ROCK), 0.45) : new THREE.Color(ROCK_DARK);
  target.copy(tintDark).lerp(tintLight, 0.25 + placement.shade * 0.6);
  const heightT = THREE.MathUtils.clamp((groundY - TERRAIN_BASE_Y) / 18, 0, 1);
  target.lerp(ROCK_VALLEY_SHADOW, (1 - heightT) * 0.32);
  target.lerp(ROCK_RIDGE_LIT, heightT * 0.45);
  return target;
}

function defaultArchetypeGeometry(archetype: RockArchetype): THREE.BufferGeometry {
  switch (archetype) {
    case 'spire':
      return makeSpireGeometry();
    case 'mesa':
      return makeMesaGeometry();
    case 'boulder':
      return makeBoulderGeometry();
    case 'archFragment':
      return makeArchFragmentGeometry();
  }
}

function makeSpireGeometry(): THREE.BufferGeometry {
  // Tall narrow with a sharp top. Unit-sized: 1 wide, 1 tall, base at y=0.
  const geom = new THREE.CylinderGeometry(0.18, 1.0, 1.0, 7, 5);
  geom.translate(0, 0.5, 0);
  roughenGeometry(geom, 0.16, 0xa17c);
  geom.computeVertexNormals();
  return geom;
}

function makeMesaGeometry(): THREE.BufferGeometry {
  const geom = new THREE.CylinderGeometry(0.78, 1.0, 1.0, 9, 3);
  geom.translate(0, 0.5, 0);
  roughenGeometry(geom, 0.1, 0x4ad2);
  geom.computeVertexNormals();
  return geom;
}

function makeBoulderGeometry(): THREE.BufferGeometry {
  const geom = new THREE.SphereGeometry(0.55, 8, 6);
  geom.scale(1.1, 0.85, 1.0);
  geom.translate(0, 0.45, 0);
  roughenGeometry(geom, 0.16, 0x9133);
  geom.computeVertexNormals();
  return geom;
}

function makeArchFragmentGeometry(): THREE.BufferGeometry {
  const geom = new THREE.TorusGeometry(0.42, 0.16, 6, 14, Math.PI * 1.1);
  geom.rotateX(Math.PI / 2);
  geom.rotateZ(Math.PI * 0.18);
  geom.translate(0, 0.55, 0);
  roughenGeometry(geom, 0.06, 0xc3a8);
  geom.computeVertexNormals();
  return geom;
}

// ────────────────────────────────────────────────────────────────────────────
// Hero landmarks (placed individually at the sharpest corners)
// ────────────────────────────────────────────────────────────────────────────

function buildHeroLandmarks(
  track: Track,
  kit: SceneryKit,
  sampler: FloorSampler,
  obstacles: ObstacleObb[],
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hero-landmarks';
  const rockMat = new THREE.MeshStandardMaterial({
    color: ROCK,
    roughness: 1,
    metalness: 0,
    flatShading: true,
  });

  for (const spec of __test__generateHeroLandmarks(track)) {
    const landmark = new THREE.Group();
    landmark.name = `hero-${spec.archetype}-${spec.centerlineIndex}`;
    landmark.position.set(spec.x, sampler(spec.x, spec.z) - 0.08, spec.z);
    landmark.rotation.y = spec.rotationY;

    landmark.add(makeHeroFoundation(spec));
    landmark.add(makeHeroRockDressing(spec));

    const obj = pickHeroObject(spec, kit, rockMat);
    if (spec.archetype === 'megaMesa') {
      obj.scale.setScalar(spec.radius);
    } else {
      obj.scale.set(spec.scaleX ?? spec.radius, spec.height, spec.scaleZ ?? spec.radius);
    }
    obj.position.y = 0.12;
    obj.name = `hero-model-${spec.archetype}-${spec.centerlineIndex}`;
    landmark.add(obj);

    group.add(landmark);
    // OBB matches the visual footprint, oriented with the landmark's Y rotation.
    // Y rotation in three.js: a +X local axis maps to (cos, -sin) in (X, Z).
    const cosR = Math.cos(spec.rotationY);
    const sinR = Math.sin(spec.rotationY);
    const footprintX = spec.scaleX ?? spec.radius;
    const footprintZ = spec.scaleZ ?? spec.radius;
    obstacles.push({
      centerX: spec.x,
      centerZ: spec.z,
      axisAcrossX: cosR,
      axisAcrossZ: -sinR,
      halfAcross: footprintX * 0.85,
      halfAlong: footprintZ * 0.85,
    });
  }
  return group;
}

function makeHeroFoundation(spec: HeroLandmarkSpec): THREE.Object3D {
  const isArena = spec.archetype === 'megaMesa';
  const group = new THREE.Group();
  group.name = isArena ? 'arena-sandstone-foundation' : 'tower-rock-foundation';

  const mat = new THREE.MeshStandardMaterial({
    color: isArena ? 0x8f5738 : ROCK_DARK,
    roughness: 0.96,
    metalness: 0,
    flatShading: true,
  });

  const footprintX = spec.scaleX ?? spec.radius;
  const footprintZ = spec.scaleZ ?? spec.radius;

  if (isArena) {
    const plinthHeight = 0.85;
    const plinth = new THREE.Mesh(
      new THREE.BoxGeometry(footprintX, plinthHeight, footprintZ, 3, 1, 8),
      mat,
    );
    roughenGeometry(plinth.geometry, 0.08, spec.centerlineIndex * 41 + 7);
    plinth.position.y = plinthHeight * 0.5 - 0.18;
    plinth.castShadow = true;
    plinth.receiveShadow = true;
    group.add(plinth);

    const wallMat = new THREE.MeshStandardMaterial({
      color: GANTRY,
      roughness: 0.92,
      metalness: 0,
      flatShading: true,
    });
    const retainingWall = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 2.2, footprintZ * 1.08, 1, 2, 8),
      wallMat,
    );
    retainingWall.position.set((spec.wallSide ?? 1) * footprintX * 0.52, 0.9, 0);
    retainingWall.castShadow = true;
    retainingWall.receiveShadow = true;
    group.add(retainingWall);

    return group;
  }

  const plinthHeight = isArena ? 0.9 : 0.65;
  const plinth = new THREE.Mesh(
    new THREE.CylinderGeometry(1, 1.08, plinthHeight, isArena ? 14 : 9, 2),
    mat,
  );
  roughenGeometry(plinth.geometry, isArena ? 0.08 : 0.12, spec.centerlineIndex * 41 + 7);
  plinth.scale.set(
    isArena ? footprintX * 1.22 : footprintX * 1.05,
    1,
    isArena ? footprintZ * 1.06 : footprintZ * 0.82,
  );
  plinth.position.y = plinthHeight * 0.5 - 0.16;
  plinth.castShadow = true;
  plinth.receiveShadow = true;
  group.add(plinth);

  if (isArena) {
    const skirt = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 1.18, 0.32, 16, 1),
      mat,
    );
    roughenGeometry(skirt.geometry, 0.05, spec.centerlineIndex * 53 + 11);
    skirt.scale.set(footprintX * 1.5, 1, footprintZ * 1.16);
    skirt.position.y = 0.02;
    skirt.castShadow = true;
    skirt.receiveShadow = true;
    group.add(skirt);
  }

  return group;
}

function makeHeroRockDressing(spec: HeroLandmarkSpec): THREE.Object3D {
  const group = new THREE.Group();
  group.name = 'hero-base-rock-dressing';
  const rng = mulberry32(spec.centerlineIndex * 997 + (spec.archetype === 'megaMesa' ? 19 : 31));
  const count = spec.archetype === 'megaMesa' ? 7 : 4;
  const footprintX = spec.scaleX ?? spec.radius;
  const footprintZ = spec.scaleZ ?? spec.radius;
  const ringX = spec.archetype === 'megaMesa' ? footprintX * 0.82 : footprintX * 1.15;
  const ringZ = spec.archetype === 'megaMesa' ? footprintZ * 0.62 : footprintZ * 1.15;
  const mat = new THREE.MeshStandardMaterial({
    color: ROCK,
    roughness: 1,
    metalness: 0,
    flatShading: true,
  });

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + rng() * 0.72;
    const size = spec.archetype === 'megaMesa'
      ? 1.4 + rng() * 2.0
      : 0.8 + rng() * 1.1;
    const rock = new THREE.Mesh(makeBoulderGeometry(), mat);
    rock.name = 'hero-foundation-boulder';
    rock.scale.set(size * (0.9 + rng() * 0.45), size * (0.45 + rng() * 0.45), size);
    rock.position.set(
      Math.cos(angle) * ringX * (0.82 + rng() * 0.32),
      0.08,
      Math.sin(angle) * ringZ * (0.82 + rng() * 0.32),
    );
    rock.rotation.y = rng() * Math.PI * 2;
    rock.castShadow = true;
    rock.receiveShadow = true;
    group.add(rock);
  }

  return group;
}

function pickHeroObject(
  spec: HeroLandmarkSpec,
  kit: SceneryKit,
  rockMat: THREE.Material,
): THREE.Object3D {
  // Authored GLB hero takes priority — preserves materials and detail.
  const authored = spec.archetype === 'megaMesa' ? kit.heroArena : kit.heroTower;
  if (authored) {
    const cloned = authored.clone(true);
    cloned.traverse((child) => {
      const m = child as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
    return cloned;
  }

  // Procedural fallback — use BufferGeometry archetypes as before.
  const geom = spec.archetype === 'megaSpire'
    ? (kit.megaSpire ?? kit.spire ?? makeSpireGeometry())
    : (kit.megaMesa ?? kit.mesa ?? makeMesaGeometry());
  const mesh = new THREE.Mesh(geom, rockMat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ────────────────────────────────────────────────────────────────────────────
// Pass-through arches (drive-under)
// ────────────────────────────────────────────────────────────────────────────

function buildPassThroughArches(track: Track, sampler: FloorSampler): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pass-through-rock-arches';
  const mat = new THREE.MeshStandardMaterial({
    color: ARCH_ROCK,
    roughness: 1,
    metalness: 0,
    flatShading: true,
  });

  for (const spec of __test__generateOverpasses(track)) {
    const arch = new THREE.Group();
    arch.name = 'drive-under-rock-arch';
    const groundY = sampler(spec.x, spec.z);
    arch.position.set(spec.x, groundY, spec.z);
    arch.rotation.y = spec.heading;

    const legRadius = 2.8;
    const legHeight = spec.clearance + 1.8;
    const left = makeRockColumn(legRadius, legHeight, mat);
    const right = makeRockColumn(legRadius * 1.08, legHeight * 0.96, mat);
    left.position.set(-spec.span * 0.5, legHeight * 0.5, 0);
    right.position.set(spec.span * 0.5, legHeight * 0.48, 0.5);

    const bridgeGeom = new THREE.BoxGeometry(spec.span + 7, 4.2, spec.depth, 4, 1, 3);
    roughenGeometry(bridgeGeom, 0.72, 0xabc + Math.floor(spec.span * 10));
    const bridge = new THREE.Mesh(bridgeGeom, mat);
    bridge.position.set(0, spec.clearance + 3.1, 0);
    bridge.castShadow = true;
    bridge.receiveShadow = true;

    arch.add(left, right, bridge);
    group.add(arch);
  }
  return group;
}

// ────────────────────────────────────────────────────────────────────────────
// Race vocabulary: gantry, sector flags, distance pylons
// ────────────────────────────────────────────────────────────────────────────

/**
 * Walks an Object3D's mesh hierarchy and extracts every triangle whose
 * world-Y range overlaps [yMin, yMax], projected to the XZ plane. The
 * resulting MeshCollider can drive fallback mesh-vs-racer collision for GLBs
 * that do not have authored proxy obstacles.
 *
 * Y filtering excludes overhead parts (arch beams, banners). The optional
 * `corridorExclusion` further excludes triangles whose centroid sits
 * INSIDE the racing corridor at this location — necessary for tunnel-style
 * bridges where mesh triangles at racer height span the entire opening but
 * should not block the racing line.
 */
export function extractMeshColliderAtRacerHeight(
  object: THREE.Object3D,
  yMin: number,
  yMax: number,
  corridorExclusion?: {
    centerX: number;
    centerZ: number;
    /** Unit vector perpendicular to the corridor axis (= track tangent direction). */
    perpX: number;
    perpZ: number;
    /** Half-width of the passable corridor. Triangles with centroid within
     *  this lateral distance of (centerX, centerZ) are excluded. */
    halfWidth: number;
  },
): MeshCollider {
  const triangles: MeshTriangle2D[] = [];
  object.updateMatrixWorld(true);
  const vA = new THREE.Vector3();
  const vB = new THREE.Vector3();
  const vC = new THREE.Vector3();
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const geom = mesh.geometry;
    const pos = geom.attributes.position as THREE.BufferAttribute | undefined;
    if (!pos) return;
    const idx = geom.index;
    const matrix = mesh.matrixWorld;
    const triCount = idx ? Math.floor(idx.count / 3) : Math.floor(pos.count / 3);
    for (let i = 0; i < triCount; i++) {
      const i0 = idx ? idx.getX(i * 3) : i * 3;
      const i1 = idx ? idx.getX(i * 3 + 1) : i * 3 + 1;
      const i2 = idx ? idx.getX(i * 3 + 2) : i * 3 + 2;
      vA.fromBufferAttribute(pos, i0).applyMatrix4(matrix);
      vB.fromBufferAttribute(pos, i1).applyMatrix4(matrix);
      vC.fromBufferAttribute(pos, i2).applyMatrix4(matrix);
      const tMinY = Math.min(vA.y, vB.y, vC.y);
      const tMaxY = Math.max(vA.y, vB.y, vC.y);
      if (tMaxY < yMin || tMinY > yMax) continue;

      if (corridorExclusion) {
        const cx = (vA.x + vB.x + vC.x) / 3;
        const cz = (vA.z + vB.z + vC.z) / 3;
        const dx = cx - corridorExclusion.centerX;
        const dz = cz - corridorExclusion.centerZ;
        const lateral = Math.abs(dx * corridorExclusion.perpX + dz * corridorExclusion.perpZ);
        if (lateral < corridorExclusion.halfWidth) continue;
      }

      const minX = Math.min(vA.x, vB.x, vC.x);
      const maxX = Math.max(vA.x, vB.x, vC.x);
      const minZ = Math.min(vA.z, vB.z, vC.z);
      const maxZ = Math.max(vA.z, vB.z, vC.z);
      if (maxX - minX < 1e-3 && maxZ - minZ < 1e-3) continue;
      triangles.push({
        ax: vA.x, az: vA.z,
        bx: vB.x, bz: vB.z,
        cx: vC.x, cz: vC.z,
        minX, minZ, maxX, maxZ,
      });
    }
  });
  return buildMeshCollider(triangles);
}

/**
 * Solid collision boxes for the authored start-finish bridge legs. The bridge
 * visual is allowed to stay high-detail, but gameplay collision uses two
 * stable OBB proxies near the outer legs so low frame rates cannot tunnel
 * through individual GLB triangles.
 */
export function buildStartBridgeProxyObstacles(track: Track): ObstacleObb[] {
  const c = track.centerline[0];
  const tangent = track.tangents[0];
  const axisAcrossX = tangent.tz;
  const axisAcrossZ = -tangent.tx;
  const corridor = c.widthLeft + c.widthRight + track.wallBuffer * 2;
  const span = corridor + 14;
  const legOffset = span * 0.5 - START_BRIDGE_PROXY_INSET;

  return [-1, 1].map((side) => ({
    centerX: c.x + axisAcrossX * legOffset * side,
    centerZ: c.z + axisAcrossZ * legOffset * side,
    axisAcrossX,
    axisAcrossZ,
    halfAcross: START_BRIDGE_PROXY_HALF_ACROSS,
    halfAlong: START_BRIDGE_PROXY_HALF_ALONG,
  }));
}

/**
 * Returns gameplay proxies for the authored bridge only. The procedural
 * fallback gantry remains decorative, matching its previous behavior.
 */
export function buildStartBridgeObstacles(track: Track, kit: SceneryKit): ObstacleObb[] {
  return kit.heroStartBridge ? buildStartBridgeProxyObstacles(track) : [];
}

function buildStartFinishGantry(
  track: Track,
  kit: SceneryKit = {},
  obstacles: ObstacleObb[] = [],
  meshColliders: MeshCollider[] = [],
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'start-finish-gantry';
  const c = track.centerline[0];
  const tangent = track.tangents[0];
  const heading = Math.atan2(tangent.tx, tangent.tz);
  const corridor = c.widthLeft + c.widthRight + track.wallBuffer * 2;
  const span = corridor + 14;
  const clearance = 9;

  // Authored bridge model (desert sci-fi). normalizeForHero() makes it a
  // unit cube — the longest authored dimension is exactly 1 unit and the
  // base sits on y=0. Uniform scale preserves the bridge's intrinsic
  // proportions; assumes its longest axis is the span across the road,
  // which holds for the desert sci-fi bridge model.
  if (kit.heroStartBridge) {
    const bridge = kit.heroStartBridge.clone();
    bridge.position.set(c.x, 0, c.z);
    // The model's long axis runs along its local Z. We want that axis to
    // span ACROSS the track (perpendicular to the racing direction), so
    // rotate an extra 90° on top of the tangent heading.
    bridge.rotation.y = heading + Math.PI / 2;
    bridge.scale.setScalar(span);
    removeStartBridgeLowDeckFaces(bridge, corridor / span / 2);
    bridge.traverse((child) => {
      const m = child as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
    group.add(bridge);
    const proxyObstacles = buildStartBridgeObstacles(track, kit);
    obstacles.push(...proxyObstacles);
    if (proxyObstacles.length === 0) {
      // Fallback for future authored bridges that cannot produce stable
      // proxies. Normal gameplay should use OBB obstacles above.
      const collider = extractMeshColliderAtRacerHeight(bridge, -0.5, 4.0, {
        centerX: c.x,
        centerZ: c.z,
        perpX: -tangent.tz,
        perpZ: tangent.tx,
        halfWidth: corridor * 0.5,
      });
      if (collider.triangles.length > 0) meshColliders.push(collider);
    }
    return group;
  }

  const stoneMat = new THREE.MeshStandardMaterial({
    color: GANTRY,
    roughness: 0.95,
    metalness: 0,
    flatShading: true,
  });

  group.position.set(c.x, 0, c.z);
  group.rotation.y = heading;

  const legRadius = 1.2;
  const left = new THREE.Mesh(
    new THREE.CylinderGeometry(legRadius, legRadius * 1.15, clearance, 6),
    stoneMat,
  );
  const right = new THREE.Mesh(
    new THREE.CylinderGeometry(legRadius, legRadius * 1.15, clearance, 6),
    stoneMat,
  );
  left.position.set(-span * 0.5, clearance * 0.5, 0);
  right.position.set(span * 0.5, clearance * 0.5, 0);
  left.castShadow = true;
  right.castShadow = true;

  const beam = new THREE.Mesh(new THREE.BoxGeometry(span, 1.4, 1.6), stoneMat);
  beam.position.set(0, clearance + 0.7, 0);
  beam.castShadow = true;

  // Checkered banner hanging from the beam.
  const bannerWidth = span * 0.7;
  const bannerHeight = 1.6;
  const cols = 16;
  const rows = 4;
  const bannerGeom = new THREE.PlaneGeometry(bannerWidth, bannerHeight, cols, rows);
  const bannerColors = new Float32Array(bannerGeom.attributes.position.count * 3);
  for (let yi = 0; yi <= rows; yi++) {
    for (let xi = 0; xi <= cols; xi++) {
      const idx = yi * (cols + 1) + xi;
      const checker = ((xi + yi) % 2 === 0) ? 1 : 0;
      bannerColors[idx * 3 + 0] = checker;
      bannerColors[idx * 3 + 1] = checker;
      bannerColors[idx * 3 + 2] = checker;
    }
  }
  bannerGeom.setAttribute('color', new THREE.BufferAttribute(bannerColors, 3));
  const banner = new THREE.Mesh(
    bannerGeom,
    new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }),
  );
  banner.position.set(0, clearance - 0.2, 0.85);

  group.add(left, right, beam, banner);
  return group;
}

function removeStartBridgeLowDeckFaces(bridge: THREE.Object3D, corridorHalfU: number): number {
  bridge.updateMatrixWorld(true);
  let removed = 0;

  bridge.traverse((child) => {
    const mesh = child as THREE.Mesh<THREE.BufferGeometry>;
    if (!mesh.isMesh || !mesh.geometry) return;

    const geometry = mesh.geometry;
    const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!position) return;

    const index = geometry.getIndex();
    const triangleCount = index ? index.count / 3 : Math.floor(position.count / 3);
    const kept: number[] = [];
    let meshRemoved = 0;

    for (let i = 0; i < triangleCount; i++) {
      const ia = index ? index.getX(i * 3) : i * 3;
      const ib = index ? index.getX(i * 3 + 1) : i * 3 + 1;
      const ic = index ? index.getX(i * 3 + 2) : i * 3 + 2;
      if (isStartBridgeLowDeckTriangle(bridge, mesh, position, ia, ib, ic, corridorHalfU)) {
        meshRemoved += 1;
      } else {
        kept.push(ia, ib, ic);
      }
    }

    if (meshRemoved === 0) return;

    const filtered = geometry.clone();
    filtered.setIndex(new THREE.BufferAttribute(new Uint32Array(kept), 1));
    mesh.geometry = filtered;
    removed += meshRemoved;
  });

  return removed;
}

function isStartBridgeLowDeckTriangle(
  bridge: THREE.Object3D,
  mesh: THREE.Mesh,
  position: THREE.BufferAttribute,
  ia: number,
  ib: number,
  ic: number,
  corridorHalfU: number,
): boolean {
  const a = bridgeLocalVertex(bridge, mesh, position, ia);
  const b = bridgeLocalVertex(bridge, mesh, position, ib);
  const c = bridgeLocalVertex(bridge, mesh, position, ic);
  const maxY = Math.max(a.y, b.y, c.y);
  if (maxY > START_BRIDGE_LOW_DECK_MAX_Y) return false;

  const centroidZ = (a.z + b.z + c.z) / 3;
  if (Math.abs(centroidZ) > corridorHalfU + START_BRIDGE_CORRIDOR_MARGIN_U) return false;

  const normal = new THREE.Vector3()
    .subVectors(b, a)
    .cross(new THREE.Vector3().subVectors(c, a))
    .normalize();
  return Math.abs(normal.y) >= START_BRIDGE_LOW_DECK_NORMAL_Y;
}

function bridgeLocalVertex(
  bridge: THREE.Object3D,
  mesh: THREE.Mesh,
  position: THREE.BufferAttribute,
  index: number,
): THREE.Vector3 {
  const v = new THREE.Vector3().fromBufferAttribute(position, index);
  mesh.localToWorld(v);
  bridge.worldToLocal(v);
  return v;
}

function buildSectorFlags(track: Track): THREE.Group {
  const group = new THREE.Group();
  group.name = 'sector-flags';
  const fractions = [0, 0.333, 0.667];
  const poleMat = new THREE.MeshStandardMaterial({
    color: PYLON,
    roughness: 0.9,
    metalness: 0,
  });
  const flagMat = new THREE.MeshBasicMaterial({
    color: FLAG_PRIMARY,
    side: THREE.DoubleSide,
  });
  const startFlagMat = new THREE.MeshBasicMaterial({
    color: FLAG_SECONDARY,
    side: THREE.DoubleSide,
  });

  fractions.forEach((fraction, sectorIndex) => {
    const idx = Math.floor(track.centerline.length * fraction) % track.centerline.length;
    const c = track.centerline[idx];
    const tangent = track.tangents[idx];
    const nx = -tangent.tz;
    const nz = tangent.tx;
    for (const side of [1, -1] as const) {
      const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
      const offset = halfWidth + track.wallBuffer + 2.6;
      const x = c.x + nx * side * offset;
      const z = c.z + nz * side * offset;
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.12, 5.4, 5),
        poleMat,
      );
      pole.position.set(x, 2.7, z);
      const flagGeom = new THREE.PlaneGeometry(2.0, 0.9);
      flagGeom.translate(1.0, 0, 0);
      const flag = new THREE.Mesh(
        flagGeom,
        sectorIndex === 0 ? startFlagMat : flagMat,
      );
      flag.position.set(x, 4.6, z);
      flag.rotation.y = Math.atan2(tangent.tx, tangent.tz);
      group.add(pole, flag);
    }
  });
  return group;
}

function buildDistancePylons(track: Track, sampler: FloorSampler): THREE.Group {
  const group = new THREE.Group();
  group.name = 'distance-pylons';
  const n = track.centerline.length;
  const stride = Math.max(8, Math.floor(n / 36));
  const poleMat = new THREE.MeshStandardMaterial({
    color: PYLON,
    roughness: 0.9,
    metalness: 0,
  });
  const bandMat = new THREE.MeshBasicMaterial({ color: PYLON_BAND });

  for (let i = 0; i < n; i += stride) {
    const a = track.tangents[(i - 6 + n) % n];
    const b = track.tangents[(i + 6) % n];
    const dot = THREE.MathUtils.clamp(a.tx * b.tx + a.tz * b.tz, -1, 1);
    const turn = Math.acos(dot);
    if (turn > 0.18) continue; // Pylons line straights only.

    const c = track.centerline[i];
    const tangent = track.tangents[i];
    const nx = -tangent.tz;
    const nz = tangent.tx;
    for (const side of [1, -1] as const) {
      const halfWidth = side > 0 ? c.widthLeft : c.widthRight;
      const offset = halfWidth + track.wallBuffer + 4;
      const x = c.x + nx * side * offset;
      const z = c.z + nz * side * offset;
      const baseY = sampler(x, z);
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18, 0.22, 4.2, 5),
        poleMat,
      );
      pole.position.set(x, baseY + 2.1, z);
      const band = new THREE.Mesh(
        new THREE.CylinderGeometry(0.24, 0.24, 0.35, 5),
        bandMat,
      );
      band.position.set(x, baseY + 3.9, z);
      group.add(pole, band);
    }
  }
  return group;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function makeRockColumn(radius: number, height: number, mat: THREE.Material): THREE.Mesh {
  const geom = new THREE.CylinderGeometry(radius * 0.74, radius, height, 7, 4);
  roughenGeometry(geom, radius * 0.18 + height * 0.025, Math.floor(radius * 1000 + height * 97));
  return new THREE.Mesh(geom, mat);
}

function pickRockArchetype(
  rng: () => number,
  biome: number,
  distFromCorridor: number,
  version: LandscapeVersion = 'classic',
): RockArchetype {
  const r = rng();
  if (isCanyonLandscape(version)) {
    if (distFromCorridor < 80) {
      if (r < 0.62) return 'boulder';
      return 'mesa';
    }
    if (biome > 0.68 && distFromCorridor > 140) {
      if (r < 0.34) return 'spire';
      if (r < 0.78) return 'mesa';
      return 'boulder';
    }
    if (r < 0.5) return 'mesa';
    return 'boulder';
  }
  if (distFromCorridor < 10) {
    if (r < 0.55) return 'boulder';
    if (r < 0.85) return 'archFragment';
    return 'mesa';
  }
  if (biome > 0.62) {
    if (r < 0.7) return 'spire';
    if (r < 0.9) return 'mesa';
    return 'boulder';
  }
  if (r < 0.32) return 'spire';
  if (r < 0.62) return 'mesa';
  if (r < 0.85) return 'boulder';
  return 'archFragment';
}

function archetypeSize(
  archetype: RockArchetype,
  rng: () => number,
  biome: number,
  distFromCorridor: number,
): { radius: number; height: number } {
  const farBoost = THREE.MathUtils.clamp(distFromCorridor / 40, 0, 1);
  switch (archetype) {
    case 'spire':
      return {
        radius: 1.5 + rng() * 2.0,
        height: 8 + rng() * 14 + biome * 8 + farBoost * 6,
      };
    case 'mesa':
      return {
        radius: 4 + rng() * 6 + biome * 2,
        height: 4 + rng() * 7 + biome * 3,
      };
    case 'boulder':
      return {
        radius: 1.4 + rng() * 2.8,
        height: 1.4 + rng() * 2.6,
      };
    case 'archFragment':
      return {
        radius: 3 + rng() * 4,
        height: 4 + rng() * 5,
      };
  }
}

function roughenGeometry(geom: THREE.BufferGeometry, amount: number, seed: number): void {
  const rng = mulberry32(seed);
  const pos = geom.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const lateral = new THREE.Vector2(x, z);
    if (lateral.lengthSq() > 0.0001) {
      lateral.normalize().multiplyScalar((rng() - 0.5) * amount);
      pos.setXYZ(i, x + lateral.x, y + (rng() - 0.5) * amount * 0.35, z + lateral.y);
    }
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
}

function desertOpenness(distance: number): number {
  return smoothstep(0.25, 0.82, Math.sin(distance * 0.011 + 1.2) * 0.5 + 0.5);
}

function circularDistance(a: number, b: number, n: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, n - d);
}

function fbm2(x: number, z: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    value += amplitude * smoothValueNoise(x * frequency, z * frequency);
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value / Math.max(norm, 1e-6);
}

function ridgeFbm(x: number, z: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const n = 1 - Math.abs(smoothValueNoise(x * frequency, z * frequency) * 2 - 1);
    value += amplitude * n * n;
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value / Math.max(norm, 1e-6);
}

function smoothValueNoise(x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = x - xi;
  const zf = z - zi;
  const u = xf * xf * (3 - 2 * xf);
  const v = zf * zf * (3 - 2 * zf);
  const a = hash2i(xi, zi);
  const b = hash2i(xi + 1, zi);
  const c = hash2i(xi, zi + 1);
  const d = hash2i(xi + 1, zi + 1);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

function hash2i(x: number, z: number): number {
  let h = x * 374761393 + z * 668265263;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
